import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { IUserPreferences, UserPreferencesModel } from '../../shared/models/user-preferences.model';
import { map, retry, tap } from 'rxjs/operators';
import { IElasticSearchResponse, IESResponse } from '../../shared/interfaces/elastic-search.interface';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private elasticSearchRecordId: string | null = null;

  constructor(private readonly http: HttpClient) {}

  getPreferences(userId: number): Observable<IUserPreferences> {
    return this.http
      .get<IElasticSearchResponse<IESUserPreferencesRecord>>(
        `${environment.elastic_search_url}/user-preferences/_search`,
        {
          params: {
            source_content_type: 'application/json',
            source: JSON.stringify({
              query: {
                term: {
                  user_id: userId,
                },
              },
            }),
          },
          headers: new HttpHeaders({
            Authorization: `ApiKey ${environment.elastic_search_api_key}`,
          }),
        },
      )
      .pipe(
        retry({ count: 2, delay: 5000 }),
        map((res) => {
          this.elasticSearchRecordId = res?.hits?.hits[0]?._id;
          return this.extractPreferences(res?.hits?.hits[0]?._source?.preferences);
        }),
        map((preferences) => new UserPreferencesModel(preferences)),
      );
  }

  savePreferences(data: IUserPreferences, userId: number): Observable<IESResponse> {
    return this.http
      .post<IESResponse>(
        `${environment.elastic_search_url}/user-preferences/_doc/${this.elasticSearchRecordId ?? ''}`,
        {
          user_id: userId,
          preferences: this.stringifyPreferences(data),
        },
        {
          headers: new HttpHeaders({
            Authorization: `ApiKey ${environment.elastic_search_api_key}`,
          }),
        },
      )
      .pipe(tap((res) => (this.elasticSearchRecordId = this.elasticSearchRecordId ?? res._id)));
  }

  private stringifyPreferences(data: IUserPreferences): string {
    return JSON.stringify(data, (key, value: unknown): unknown => (value === undefined ? null : value));
  }

  private extractPreferences(preferences: string): IUserPreferences {
    let parsedPreferences: IUserPreferences;

    try {
      parsedPreferences = (JSON.parse(preferences) ?? {}) as IUserPreferences;
    } catch {
      console.error('UserPreferencesService: error parsing preferences');
      parsedPreferences = {} as IUserPreferences;
    }

    return parsedPreferences;
  }
}

export interface IESUserPreferencesRecord {
  preferences: string;
  user_id: string;
}
